import Supercluster, { PointFeature } from 'supercluster';
import { NavigateFunction } from 'react-router-dom';
import { ID } from '@app/types';
import Contact from '@models/Contact';
import { ThemeName } from '@app/themes';
import { ClusterID, ContactID, createAnnotationFactory } from './Annotations';

const coordinateToMapkitCoordinate = (coordinate: [number, number]): mapkit.Coordinate =>
  new mapkit.Coordinate(coordinate[1], coordinate[0]);

const mapkitRegionToCoordinateBBox = (
  region: mapkit.CoordinateRegion,
): [number, number, number, number] => {
  const { center, span } = region;

  // use value less than 2 so that we calculate the a slightly larger bounding box
  // It could be that this should depend on the aspect ratio of the map display?
  const westLng = center.longitude - span.longitudeDelta / 1.7;
  const eastLng = center.longitude + span.longitudeDelta / 1.7;
  const northLat = center.latitude + span.latitudeDelta / 1.3;
  const southLat = center.latitude - span.latitudeDelta / 1.3;

  return [westLng, southLat, eastLng, northLat];
};

interface PointProps {
  id: ID;
  displayName: string;
  avatarURL: string;
}

export default class ClayMapkit {
  clusterStore: Supercluster<PointProps>;
  $mapContainer: HTMLDivElement;
  map: mapkit.Map;
  selectContact: (id: ContactID) => void;
  selectCluster: (id: ClusterID, contactIDs: ContactID[]) => void;
  annotationFactory: (coordinate: mapkit.Coordinate, options: any) => HTMLElement;
  cleanup: () => void;

  constructor({
    theme,
    mapContainerNode,
    visibleMapRect,
    selectContact,
    selectCluster,
    navigate,
    onZoom,
    onRegionChange,
  }: {
    theme: ThemeName;
    mapContainerNode: HTMLDivElement;
    visibleMapRect: mapkit.MapRect | null;
    selectContact: (id: ContactID) => void;
    selectCluster: (id: ClusterID, contactIDs: ContactID[]) => void;
    navigate: NavigateFunction;
    onZoom: () => void;
    onRegionChange: () => void;
  }) {
    this.clusterStore = new Supercluster({ radius: 120 });
    this.$mapContainer = mapContainerNode;
    this.map = new mapkit.Map(mapContainerNode, {
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsMapTypeControl: false,
      colorScheme: theme === 'light' ? 'light' : 'dark',
      mapType: mapkit.Map.MapTypes.MutedStandard,
      visibleMapRect: visibleMapRect || new mapkit.MapRect(0, 0, 1, 1),
      showsZoomControl: false,
    });

    // @ts-ignore
    this.map._allowWheelToZoom = true;
    this.selectContact = selectContact;
    this.selectCluster = selectCluster;
    this._setAnnotationFactory(navigate);
    this.cleanup = this._addMapListeners({ onZoom, onRegionChange });
  }

  _addMapListeners({ onZoom, onRegionChange }: { onZoom: () => void; onRegionChange: () => void }) {
    const onRegionChangeEnd = () => {
      this._updateRenderedAnnotations();
      onRegionChange();
    };

    const onZoomEnd = () => {
      onZoom();
    };

    const onWindowResize = () => {
      this._updateRenderedAnnotations();
    };

    this.map.addEventListener('region-change-end', onRegionChangeEnd);
    this.map.addEventListener('zoom-end', onZoomEnd);
    window.addEventListener('resize', onWindowResize);

    return () => {
      this.map.removeEventListener('region-change-end', onRegionChangeEnd);
      this.map.removeEventListener('zoom-end', onZoomEnd);
      window.removeEventListener('resize', onWindowResize);
    };
  }

  _loadContactsIntoClusterStore(contacts: Contact[]) {
    const contactsAsFeatures: Supercluster.PointFeature<PointProps>[] = contacts
      .filter((contact) => !!contact.coordinate)
      .map((contact) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: contact.coordinate },
        properties: {
          id: contact.id,
          displayName: contact.displayName,
          avatarURL: contact.avatarURL,
          coordinates: contact.coordinate,
        },
      }));

    this.clusterStore.load(contactsAsFeatures);
  }

  _getClustersForView() {
    try {
      return this.clusterStore.getClusters(
        mapkitRegionToCoordinateBBox(this.map.region),
        // @ts-ignore
        Math.round(this.map._impl.zoomLevel),
      );
    } catch (error) {
      return [];
    }
  }

  _selectClusterAndChildren(clusterID: number) {
    this.selectCluster(clusterID, this._getContactIDsForCluster(clusterID));
  }

  _setAnnotationFactory(navigate: NavigateFunction) {
    this.annotationFactory = createAnnotationFactory({
      selectContact: this.selectContact,
      selectCluster: this._selectClusterAndChildren.bind(this),
      navigateToContact: (id: ContactID) => navigate(`/contact/${id}`),
    });
  }

  _renderClustersAsAnnotations(
    clusters: (
      | Supercluster.ClusterFeature<Supercluster.AnyProps>
      | Supercluster.PointFeature<Supercluster.AnyProps>
    )[],
  ) {
    this.map.removeAnnotations(this.map.annotations);

    const { clusterAnnotations, contactAnnotations } = clusters.reduce(
      (added, cluster) => {
        // @ts-ignore
        const mapkitCoordinate = coordinateToMapkitCoordinate(cluster.geometry.coordinates); // hmmmm
        let childPoints: PointFeature<PointProps>[] = [];
        const isCluster = cluster.properties.cluster;

        if (isCluster) {
          childPoints = this.clusterStore.getLeaves(cluster.properties.cluster_id, 3);
        }

        const annotation = new mapkit.Annotation(mapkitCoordinate, this.annotationFactory, {
          data: {
            isCluster,
            childPoints,
            ...cluster.properties,
          },
        });

        return {
          clusterAnnotations: isCluster
            ? [...added.clusterAnnotations, annotation]
            : added.clusterAnnotations,
          contactAnnotations: !isCluster
            ? [...added.contactAnnotations, annotation]
            : added.contactAnnotations,
        };
      },
      { clusterAnnotations: [], contactAnnotations: [] },
    );

    const sortedContactAnnotations = contactAnnotations.sort((a, b) => {
      if (a.coordinate.latitude < b.coordinate.latitude) {
        return -1;
      }

      if (a.coordinate.latitude > b.coordinate.latitude) {
        return 1;
      }

      return 0;
    });

    // this stacks the contact annotations on top of the cluster annotations in the mapkit UI -- minimizes issues with rendering of the labels
    // we may also want to sort these by latitude...
    this.map.addAnnotations(clusterAnnotations.concat(sortedContactAnnotations));
  }

  _updateRenderedAnnotations() {
    const clusters = this._getClustersForView();
    this._renderClustersAsAnnotations(clusters);
  }

  _getContactIDsForCluster(clusterID: ClusterID) {
    try {
      return this.clusterStore.getLeaves(clusterID, Infinity).map((point) => point.properties.id);
    } catch (error) {
      return [];
    }
  }

  updateNavigate(navigate: NavigateFunction) {
    this._setAnnotationFactory(navigate);
  }

  updateContacts(contacts: Contact[]) {
    this._loadContactsIntoClusterStore(contacts);
    this._updateRenderedAnnotations();
  }

  zoomIn() {
    this.map.setCameraDistanceAnimated(this.map.cameraDistance * 0.5);
  }

  zoomOut() {
    this.map.setCameraDistanceAnimated(this.map.cameraDistance * 2);
  }

  get zoomInDisabled() {
    // @ts-ignore
    return this.map._impl.zoomLevel >= 20;
  }

  get zoomOutDisabled() {
    // @ts-ignore
    return this.map._impl.zoomLevel <= 2;
  }
}
